import React, { useState } from 'react'
import { User, PasswordResetFormData } from '../../../types'
import { X, Key, Eye, EyeOff, RefreshCw } from 'lucide-react'

interface PasswordResetModalProps {
  user: User
  onSubmit: (data: PasswordResetFormData) => void
  onCancel: () => void
  isLoading: boolean
}

const PasswordResetModal: React.FC<PasswordResetModalProps> = ({
  user,
  onSubmit,
  onCancel,
  isLoading
}) => {
  const [formData, setFormData] = useState<PasswordResetFormData>({
    newPassword: '',
    confirmPassword: '',
    forcePasswordChange: true,
    sendNotification: true
  })

  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const handleInputChange = (field: keyof PasswordResetFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
    
    // 清除該欄位的錯誤
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }))
    }
  }

  const generateRandomPassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*'
    let password = ''
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length))
    }
    setFormData(prev => ({
      ...prev,
      newPassword: password,
      confirmPassword: password
    }))
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.newPassword) {
      newErrors.newPassword = '新密碼為必填欄位'
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = '密碼長度至少需要 6 個字元'
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = '確認密碼為必填欄位'
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = '密碼確認不一致'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    onSubmit(formData)
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content max-w-md w-full">
        {/* 標題 */}
        <div className="flex items-center justify-between p-6 border-b border-primary-200">
          <div className="flex items-center space-x-3">
            <Key className="w-6 h-6 text-primary-600" />
            <h2 className="text-xl font-semibold text-secondary-800">重設用戶密碼</h2>
          </div>
          <button
            onClick={onCancel}
            className="text-secondary-400 hover:text-secondary-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* 表單內容 */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* 用戶資訊 */}
          <div className="bg-primary-50 rounded-lg p-4">
            <p className="text-sm text-secondary-700">
              <span className="font-medium">成員:</span> {user.name} ({user.email})
            </p>
          </div>

          {/* 新密碼 */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              新密碼 <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={formData.newPassword}
                onChange={(e) => handleInputChange('newPassword', e.target.value)}
                className={`input-primary pr-20 ${errors.newPassword ? 'input-error' : ''}`}
                placeholder="輸入新密碼..."
              />
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
                <button
                  type="button"
                  onClick={generateRandomPassword}
                  className="text-primary-600 hover:text-primary-700 transition-colors"
                  title="產生隨機密碼"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="text-secondary-400 hover:text-secondary-600 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            {errors.newPassword && (
              <p className="text-red-500 text-sm mt-1">{errors.newPassword}</p>
            )}
          </div>

          {/* 確認密碼 */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              確認密碼 <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                className={`input-primary pr-10 ${errors.confirmPassword ? 'input-error' : ''}`}
                placeholder="再次輸入密碼..."
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-secondary-400 hover:text-secondary-600 transition-colors"
              >
                {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>
            )}
          </div>

          {/* 選項設定 */}
          <div className="space-y-3">
            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={formData.forcePasswordChange}
                onChange={(e) => handleInputChange('forcePasswordChange', e.target.checked)}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm text-secondary-700">強制用戶下次登入時更改密碼</span>
            </label>

            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={formData.sendNotification}
                onChange={(e) => handleInputChange('sendNotification', e.target.checked)}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm text-secondary-700">發送密碼變更通知信件</span>
            </label>
          </div>

          {/* 警告訊息 */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start space-x-2">
              <div className="w-5 h-5 text-yellow-600 mt-0.5">⚠️</div>
              <p className="text-sm text-yellow-800">
                密碼重設後，用戶需要使用新密碼登入
              </p>
            </div>
          </div>

          {/* 操作按鈕 */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-primary-200">
            <button
              type="button"
              onClick={onCancel}
              className="btn-secondary"
              disabled={isLoading}
            >
              取消
            </button>
            <button
              type="submit"
              className="btn-danger"
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center space-x-2">
                  <div className="loading-spinner w-4 h-4"></div>
                  <span>重設中...</span>
                </div>
              ) : (
                '重設密碼'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default PasswordResetModal
